import React, { useState, useRef, useEffect } from 'react';
import { Button, Popover, MenuItem, TextField, Checkbox, ListItemText } from '@mui/material';

interface SiteSearchMultiSelectorProps {
  sites: string[];
  selectedSites: string[];
  onChange: (selected: string[]) => void;
  label?: string;
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

export function SiteSearchMultiSelector({
  sites,
  selectedSites,
  onChange,
  label = 'Select sites',
  color = 'primary',
}: SiteSearchMultiSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggle = () => setIsOpen((o) => !o);

  const handleClose = () => {
    setIsOpen(false);
    setSearch('');
  };

  const handleSelect = (site: string) => {
    if (selectedSites.includes(site)) {
      onChange(selectedSites.filter((s) => s !== site));
    } else {
      onChange([...selectedSites, site]);
    }
  };

  const filteredSites = search ? sites.filter((site) => site.toLowerCase().includes(search.toLowerCase())) : sites;

  return (
    <div style={{ width: '100%', height: 50 }}>
      <Button
        ref={buttonRef}
        onClick={handleToggle}
        fullWidth
        variant='contained'
        style={{ height: '100%' }}
        color={color}>
        {label}
      </Button>
      <Popover
        open={isOpen}
        anchorEl={buttonRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { style: { maxHeight: 300, width: '100%', maxWidth: 400, padding: 8 } } }}>
        <TextField
          placeholder='Search sites'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size='small'
          fullWidth
          inputRef={inputRef}
          style={{ marginBottom: 8 }}
          color={color}
        />
        {filteredSites.length > 0 ? (
          filteredSites.map((site) => (
            <li key={site} style={{ padding: 0, listStyle: 'none' }}>
              <button
                type='button'
                onClick={() => handleSelect(site)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  background: 'none',
                  border: 'none',
                  width: '100%',
                  textAlign: 'left',
                }}>
                <Checkbox checked={selectedSites.includes(site)} />
                <ListItemText primary={site} />
              </button>
            </li>
          ))
        ) : (
          <MenuItem disabled>No sites found</MenuItem>
        )}
      </Popover>
    </div>
  );
}
